import { cn } from "@/lib/utils";

interface CharacterCountBarProps {
  current: number;
  min: number;
  max: number;
  optimal?: { min: number; max: number };
}

export function CharacterCountBar({
  current,
  min,
  max,
  optimal,
}: CharacterCountBarProps) {
  const getStatus = () => {
    if (optimal && current >= optimal.min && current <= optimal.max) {
      return "success";
    }
    if (current < min || current > max) return "error";
    if (optimal) return "warning";
    return "success";
  };

  const status = getStatus();
  const percentage = Math.min(Math.max((current / max) * 100, 0), 100);

  const getStatusLabel = () => {
    if (current < min) return "too short";
    if (current > max) return "too long";
    if (optimal && (current < optimal.min || current > optimal.max)) {
      return "acceptable";
    }
    return "optimal";
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">
            {current} / {max} characters
          </span>
          {optimal && (
            <span className="text-xs text-muted-foreground">
              Target {optimal.min}-{optimal.max}
            </span>
          )}
        </div>
        <span
          className={cn(
            "text-xs font-medium",
            status === "error" && "text-red-600 dark:text-red-400",
            status === "warning" && "text-yellow-600 dark:text-yellow-400",
            status === "success" && "text-green-600 dark:text-green-400",
          )}
        >
          ({getStatusLabel()})
        </span>
      </div>

      <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200">
        {/* Optimal range indicator */}
        {optimal && (
          <div
            className="absolute top-0 h-full bg-green-100 dark:bg-green-900/40"
            style={{
              left: `${(optimal.min / max) * 100}%`,
              width: `${((optimal.max - optimal.min) / max) * 100}%`,
            }}
          />
        )}

        {/* Progress bar */}
        <div
          className={cn(
            "h-full transition-all duration-300",
            status === "error" && "bg-red-500",
            status === "warning" && "bg-yellow-500",
            status === "success" && "bg-green-500",
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
